import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import CircleIcon from 'lucide-react/dist/esm/icons/circle';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { buttonVariants } from './buttonVariants';

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn('grid gap-3', className)}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        'aspect-square size-4 shrink-0 rounded-full border border-input text-primary shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:bg-input/30 dark:aria-invalid:ring-destructive/40',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 fill-primary" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

/**
 * A radio option drawn as a selectable card. The radio's hit area stretches
 * over the whole card, so a click anywhere selects it while the radio alone
 * stays the interactive element. Buttons placed inside the card must sit
 * above that overlay with `relative z-10`.
 */
function RadioGroupCard({
  value,
  checked,
  disabled = false,
  labelId,
  descriptionId,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  value: string;
  checked: boolean;
  disabled?: boolean;
  /** Id of the element that names the option. */
  labelId: string;
  /** Id of the element that describes the option. */
  descriptionId?: string;
}) {
  return (
    <div
      data-slot="radio-group-card"
      data-state={checked ? 'checked' : 'unchecked'}
      data-disabled={disabled ? '' : undefined}
      className={cn(
        'relative flex w-full items-start gap-3 rounded-md border border-input bg-background p-3 text-left text-sm shadow-xs data-disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary/5 data-[state=checked]:ring-1 data-[state=checked]:ring-primary data-[state=unchecked]:hover:bg-accent/50',
        className
      )}
      {...props}
    >
      <RadioGroupItem
        value={value}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        className="mt-0.5 before:absolute before:inset-0 before:z-0 before:cursor-pointer disabled:before:cursor-not-allowed"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
    </div>
  );
}

/**
 * A radio option drawn as an outline button that fills when checked, for a grid of short choices
 * such as times.
 */
function RadioGroupButton({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-button"
      className={cn(
        buttonVariants({ variant: 'outline' }),
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:hover:bg-primary/90',
        className
      )}
      {...props}
    />
  );
}

export { RadioGroup, RadioGroupButton, RadioGroupCard, RadioGroupItem };
